const POINTER_CONTRACT = "PWG_MLB_POINTER_1.0";
const MANIFEST_CONTRACT = "PWG_MLB_STATIC_1.0";
const BOARD_CONTRACT = "PWG_MLB_BOARD_1.0";
const DETAIL_CONTRACT = "PWG_MLB_DETAIL_1.0";

const cleanBase = (value) => String(value || "").trim().replace(/\/+$/, "");
const CUSTOMER_MLB_BASE =
  "https://storage.googleapis.com/playswithguru-27598-customer-sports-prod/mlb";
const productionHost = () => typeof window !== "undefined" &&
  /(?:^|\.)playswithguru\.com$/i.test(String(window.location.hostname || ""));
const configuredBase = () => cleanBase(
  process.env.REACT_APP_MLB_STATIC_BASE ||
  (productionHost() ? CUSTOMER_MLB_BASE : "/__local/mlb-static/customer/mlb")
);

export const mlbStaticDataEnabled = () =>
  String(process.env.REACT_APP_MLB_STATIC_SHARDS || "").trim().toLowerCase() === "true";

const caches = {
  pointer: null,
  manifests: new Map(),
  games: new Map(),
  boards: new Map(),
  details: new Map(),
};

const joinUrl = (base, path) => `${cleanBase(base)}/${String(path || "").replace(/^\/+/, "")}`;

async function fetchJson(url, { mutable = false } = {}) {
  const response = await fetch(url, {
    cache: mutable ? "no-store" : "force-cache",
    credentials: "omit",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error(`MLB static data unavailable (${response.status})`);
  return response.json();
}

function requireContract(payload, contract, label) {
  if (!payload || payload.contract_version !== contract) {
    throw new Error(`${label} contract is missing or unsupported`);
  }
  return payload;
}

function assertVersion(payload, version, label) {
  if (String(payload?.version_id || "") !== String(version || "")) {
    throw new Error(`${label} version does not match the pinned MLB release`);
  }
}

function projectionId(row) {
  return String(row?.["Projection ID"] ?? row?.ProjectionID ?? row?.projection_id ?? "").trim();
}

function normalizedLineType(row) {
  const value = String(row?.["Line Type"] || row?.["Source Line Type"] || "").trim().toLowerCase();
  return value === "primary" ? "regular" : value;
}

async function pointer() {
  if (caches.pointer) return caches.pointer;
  const payload = requireContract(
    await fetchJson(joinUrl(configuredBase(), "current.json"), { mutable: true }),
    POINTER_CONTRACT,
    "MLB pointer",
  );
  if (!payload.version_id || !payload.manifest_path) throw new Error("MLB pointer identity is incomplete");
  caches.pointer = payload;
  return payload;
}

async function manifestFor(versionId = "") {
  const current = versionId ? null : await pointer();
  const version = String(versionId || current?.version_id || "").trim();
  if (!version || !/^[A-Za-z0-9._-]+$/.test(version)) throw new Error("MLB static version is invalid");
  if (caches.manifests.has(version)) return caches.manifests.get(version);
  const path = current?.manifest_path || `releases/${version}/manifest.json`;
  const payload = requireContract(
    await fetchJson(joinUrl(configuredBase(), path)),
    MANIFEST_CONTRACT,
    "MLB manifest",
  );
  assertVersion(payload, version, "MLB manifest");
  if (!Array.isArray(payload.board_shards) || !Array.isArray(payload.detail_shards)) {
    throw new Error("MLB manifest shard inventory is incomplete");
  }
  caches.manifests.set(version, payload);
  return payload;
}

async function gamesFor(manifest) {
  const version = manifest.version_id;
  if (caches.games.has(version)) return caches.games.get(version);
  const relative = manifest.games?.path || "games.json";
  const payload = await fetchJson(joinUrl(configuredBase(), `releases/${version}/${relative}`));
  requireContract(payload, MANIFEST_CONTRACT, "MLB games");
  assertVersion(payload, version, "MLB games");
  const games = Array.isArray(payload.games) ? payload.games.map((game) => ({
    ...game,
    projection_id_count: Number(game.projection_id_count ?? game.row_count ?? 0),
    matchup: game.matchup || `${game.away_team || ""} @ ${game.home_team || ""}`.trim(),
    line_type_counts: game.line_type_counts || {},
  })) : [];
  caches.games.set(version, games);
  return games;
}

async function boardShard(version, entry) {
  const key = `${version}:${entry.game_id}`;
  if (caches.boards.has(key)) return caches.boards.get(key);
  const payload = requireContract(
    await fetchJson(joinUrl(configuredBase(), `releases/${version}/${entry.path}`)),
    BOARD_CONTRACT,
    "MLB board shard",
  );
  assertVersion(payload, version, "MLB board shard");
  if (String(payload.game_id) !== String(entry.game_id)) throw new Error("MLB board shard game identity mismatch");
  const rows = Array.isArray(payload.props) ? payload.props : [];
  if (rows.length !== Number(entry.rows)) throw new Error("MLB board shard row count mismatch");
  const value = rows.map((row) => ({ ...row, __pwgVersion: version, __pwgStaticShard: true }));
  caches.boards.set(key, value);
  return value;
}

export async function loadStaticMlbBoard({ versionId = "", lineType = "regular", gameIds = [], onProgress, onFirstPage, onPage, isCurrent } = {}) {
  const manifest = await manifestFor(versionId);
  const version = manifest.version_id;
  const games = await gamesFor(manifest);
  const requested = new Set((Array.isArray(gameIds) ? gameIds : []).map(String).filter(Boolean));
  const entries = manifest.board_shards.filter((entry) => !requested.size || requested.has(String(entry.game_id)));
  const expected = entries.reduce((sum, entry) => sum + Number(entry.rows || 0), 0);
  const rows = [];
  let published = false;
  for (const entry of entries) {
    if (isCurrent && !isCurrent()) return { props: [], games, version: { version_id: version }, page: { cancelled: true } };
    const shardRows = await boardShard(version, entry);
    const filtered = String(lineType || "all").toLowerCase() === "all"
      ? shardRows
      : shardRows.filter((row) => normalizedLineType(row) === String(lineType).toLowerCase());
    rows.push(...filtered);
    const partial = {
      props: [...rows], games, injuryReport: [], version: { version_id: version },
      page: { loaded: rows.length, total: expected, shards: entries.length },
    };
    onProgress?.(partial.page);
    onPage?.(partial);
    if (!published) {
      published = true;
      onFirstPage?.(partial);
    }
  }
  const ids = rows.map(projectionId);
  if (ids.some((id) => !id) || new Set(ids).size !== ids.length) throw new Error("MLB static board has missing or duplicate Projection IDs");
  return { props: rows, games, injuryReport: [], version: { version_id: version }, page: { loaded: rows.length, total: rows.length, shards: entries.length } };
}

async function detailShard(version, gameId, manifest) {
  const key = `${version}:${gameId}`;
  if (caches.details.has(key)) return caches.details.get(key);
  const entry = manifest.detail_shards.find((item) => String(item.game_id) === String(gameId));
  if (!entry) throw new Error("MLB detail shard is not declared in the pinned manifest");
  const payload = requireContract(
    await fetchJson(joinUrl(configuredBase(), `releases/${version}/${entry.path}`)),
    DETAIL_CONTRACT,
    "MLB detail shard",
  );
  assertVersion(payload, version, "MLB detail shard");
  if (String(payload.game_id) !== String(gameId)) throw new Error("MLB detail shard game identity mismatch");
  caches.details.set(key, payload.details || {});
  return caches.details.get(key);
}

export async function loadStaticMlbProjection(row, versionId = "") {
  const version = String(versionId || row?.__pwgVersion || "").trim();
  if (!version) throw new Error("A static MLB version is required");
  const id = projectionId(row);
  const gameId = String(row?.["Game ID"] || "").trim();
  if (!id || !gameId) return null;
  const manifest = await manifestFor(version);
  const details = await detailShard(version, gameId, manifest);
  const detail = details[id];
  if (!detail || !Array.isArray(detail.last5) || detail.last5.length > 5) return null;
  return {
    ...row,
    Last10: detail.last5,
    __pwgVersion: version,
    __pwgStaticShard: true,
    __pwgCustomerHistoryLimit: 5,
  };
}

export function __resetMlbStaticDataForTests() {
  caches.pointer = null;
  caches.manifests.clear();
  caches.games.clear();
  caches.boards.clear();
  caches.details.clear();
}
